import React from "react";
import ItemsTable from "../../components/ItemsTable";

const COLUMN_CONFIGS = {
    skuImage: { label: "SKU Image", maxWidth: 84 },
    SKUID: { label: "SKU ID", maxWidth: 160 },
    description: { label: "Description", minWidth: 220, maxWidth: 240 },
    expectedQty: { label: "Order Qty", maxWidth: 120 },
    openQty: { label: "Pending Qty", maxWidth: 120 },
    packedQty: { label: "Packed Qty", maxWidth: 120 },
    status: { label: "Status", maxWidth: 160 },
};

const BASE_COLUMNS = [
    "skuImage",
    "SKUID",
    "description",
    "expectedQty",
    "openQty",
    "packedQty",
    "status",
];

const FILTER_PLACEHOLDERS = {
    SKUID: "SKU",
    description: "Desc",
};

const PlaceholderImg = () => (
    <div className="rounded bg-light border" style={{ width: 48, height: 48 }} title="No Image" />
);

export default function PackingSearchResults({
    searchResults,
    packedBySku = {},
    readOnly = true,
}) {
    const oneTableData = React.useMemo(() => {
        const rows = [];
        if (searchResults && typeof searchResults === "object") {
            Object.entries(searchResults)
                .filter(([k]) => !k.startsWith("__"))
                .forEach(([, arr]) => {
                    if (Array.isArray(arr)) rows.push(...arr);
                });
        }
        return { ITEMS: rows };
    }, [searchResults]);

    const headerLabelFormatter = React.useCallback(() => "Items", []);

    const cellRenderer = React.useCallback(
        (column, row) => {
            const sku = String(row.skuId ?? row.item ?? "");
            const expected = Number(row.expectedQty ?? row.orderedQty ?? 0) || 0;

            const packedFromState = Number(packedBySku[sku] ?? row.packedQty ?? 0) || 0;
            const packedQty = Math.min(expected, Math.max(0, packedFromState));
            const pendingQty = Math.max(expected - packedQty, 0);

            let statusNode;
            if (packedQty === 0) {
                // nothing scanned yet
                statusNode = (
                    <span className="d-inline-flex align-items-center text-muted">
                        <i className="bi bi-hourglass-split me-1" />
                        …
                    </span>
                );
            } else if (packedQty < expected) {
                statusNode = (
                    <span className="d-inline-flex align-items-center text-warning">
                        <i className="bi bi-clock-fill me-1" />
                        In&nbsp;Progress
                    </span>
                );
            } else {
                statusNode = (
                    <span className="d-inline-flex align-items-center text-success">
                        <i className="bi bi-check-circle-fill me-1" />
                        Completed
                    </span>
                );
            }

            const cellData = {
                skuImage: row.imageUrl ? (
                    <img
                        src={row.imageUrl}
                        alt={sku || "SKU"}
                        style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }}
                    />
                ) : (
                    <PlaceholderImg />
                ),
                SKUID: sku,
                description: row.description ?? `Item ${sku}`,
                expectedQty: expected,
                openQty: pendingQty,
                packedQty,
                status: statusNode,
            };

            return cellData[column];
        },
        [packedBySku]
    );

    const filterFunction = React.useCallback(
        (row, _originalIndex, filters) => {
            const sku = String(row.skuId ?? row.item ?? "");
            const expected = Number(row.expectedQty ?? row.orderedQty ?? 0) || 0;
            const packedQty = Math.min(
                expected,
                Math.max(0, Number(packedBySku[sku] ?? row.packedQty ?? 0) || 0)
            );
            const pendingQty = Math.max(expected - packedQty, 0);

            const derivedRow = {
                ...row,
                SKUID: sku,
                description: row.description,
                expectedQty: expected,
                openQty: pendingQty,
                packedQty,
                status:
                    packedQty === 0
                        ? "Pending"
                        : packedQty < expected
                            ? "In Progress"
                            : "Completed",
            };

            return Object.entries(filters).every(([key, filterVal]) => {
                if (!filterVal) return true;
                const val = derivedRow[key];
                return String(val ?? "").toLowerCase().includes(String(filterVal).toLowerCase());
            });
        },
        [packedBySku]
    );

    const handleCellChange = React.useCallback(() => { }, []);

    return (
        <ItemsTable
            data={oneTableData}
            columns={BASE_COLUMNS}
            columnConfigs={COLUMN_CONFIGS}
            filterPlaceholders={FILTER_PLACEHOLDERS}
            cellRenderer={cellRenderer}
            headerLabelFormatter={headerLabelFormatter}
            filterFunction={filterFunction}
            onCellChange={handleCellChange}
            onRemoveGroup={null}
            onSelectionChange={null}
            readOnly={readOnly}
            autoSelectOnInput={false}
        />
    );
}
